import { Button, DatePicker, ModuleHeader } from '@/components'
import { Input } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'
import { PermissionsRequired } from '@/containers/permission-containers'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import { useAdmin, useLeaderGroups, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, isValid, startOfMonth } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import { BrowserView, isMobileOnly, MobileView } from 'react-device-detect'
import styled from 'styled-components'
import { IssuesTable } from '../components'
import { IssuePopupFilter } from '../components/IssuePopupFilter'
import type { IssuesTableFilter } from '../types'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  ${props => props.theme.layout.largePad} {
    padding: 122px 52px 52px;
    box-sizing: border-box;
  }
`
const Controls = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
`
const StyledInput = styled(Input)`
  ${isMobileOnly &&
  `
    width: 100%;
  `}
`

const DEFAULT_DATE_RANGE = {
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
}

interface IssuesProps {}

export const Issues: React.FC<IssuesProps> = () => {
  const translations = useTranslate({
    issues: 'common.issues',
    createNew: 'common.create',
    findIssue: 'issues.find-issue',
    chooseGroup: 'common.choose-group',
    amount: 'common.amount',
    date: 'common.date',
  })

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups(
    Module.ISSUES
  )
  const isAdmin = useAdmin()
  const { hasPermissions } = usePermissions()
  const canViewOthersIssue = hasPermissions(
    PERMISSIONS.issues.view.othersissue,
    true
  )

  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
  } = useForm<IssuesTableFilter>({
    values: {
      query: '',
      orderBy: '-timeOfIssueStart',
      excludeCategories: [],
      open: undefined,
      archived: false,
      severities: [],
      onlyManuallyCreated: true,
      leaderGroup: null,
      leaderGroupUsers: [],
      dateRange: DEFAULT_DATE_RANGE,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: 'firmadok__issue-overview-filter',
        transformValues: {
          dateRange: value => {
            if (
              !value ||
              typeof value !== 'object' ||
              Array.isArray(value) ||
              !('from' in value) ||
              !('to' in value)
            ) {
              return DEFAULT_DATE_RANGE
            }

            const from = new Date(value.from as string)
            const to = new Date(value.to as string)

            if (!isValid(from) || !isValid(to)) return DEFAULT_DATE_RANGE

            return { from, to }
          },
        },
      },
    },
  })
  const didInitializeLeaderGroup = useRef(false)

  const changeFilter = (value: string | null) => {
    const leaderGroup = value || null
    const selectedGroup = myLeaderGroups.find(g => g.id === leaderGroup)
    updateForm({
      leaderGroup,
      leaderGroupUsers: selectedGroup?.users.map(user => user.id) ?? [],
    })
  }

  const [filteredCount, setFilteredCount] = useState<number | undefined>(
    undefined
  )

  useEffect(() => {
    if (didInitializeLeaderGroup.current || groupsLoading) return

    if (
      !canViewOthersIssue ||
      isAdmin ||
      filter.leaderGroup ||
      myLeaderGroups.length !== 1
    ) {
      didInitializeLeaderGroup.current = true
      return
    }

    const defaultLeaderGroup = myLeaderGroups[0]
    if (!defaultLeaderGroup) {
      didInitializeLeaderGroup.current = true
      return
    }

    updateForm({
      leaderGroup: defaultLeaderGroup.id,
      leaderGroupUsers: defaultLeaderGroup.users.map(user => user.id),
    })
    didInitializeLeaderGroup.current = true
  }, [
    canViewOthersIssue,
    groupsLoading,
    filter.leaderGroup,
    isAdmin,
    myLeaderGroups,
    updateForm,
  ])

  return (
    <Wrapper>
      <ModuleHeader title={translations.issues} page={PageType.ISSUES}>
        <MobileView>
          <PermissionsRequired permissions={PERMISSIONS.issues.add.issue}>
            <Button asChild size="icon">
              <PlainLink to="/issues/create">
                <Icon icon="plus" />
              </PlainLink>
            </Button>
          </PermissionsRequired>
        </MobileView>

        <BrowserView>
          {typeof filteredCount !== 'undefined' && (
            <span className="text-primary">
              {translations.amount}: {filteredCount}
            </span>
          )}
        </BrowserView>
      </ModuleHeader>

      <Controls>
        <ButtonContainer>
          <StyledInput
            value={filter.query}
            type="search"
            placeholder={translations.findIssue}
            onChange={filterHandler('query')}
          />
          <DatePicker
            mode="range"
            value={filter.dateRange}
            format="PP"
            onChange={value => {
              if (!value?.from || !value.to) return
              filterHandler('dateRange')({ from: value.from, to: value.to })
            }}
          />

          {hasAccess && canViewOthersIssue && (
            <LeaderGroupSelect
              value={filter.leaderGroup}
              loading={groupsLoading}
              options={
                myLeaderGroups?.map(group => ({
                  label: group.name,
                  value: group.id,
                })) ?? []
              }
              fullWidth
              grayed={true}
              onChange={value => changeFilter(value)}
              placeholder={translations.chooseGroup}
              nullable
            />
          )}
        </ButtonContainer>

        <BrowserView>
          <ButtonContainer>
            <IssuePopupFilter filter={filter} onUpdate={updateForm} />

            <PermissionsRequired permissions={PERMISSIONS.issues.add.issue}>
              <Button asChild>
                <PlainLink to="/issues/create">
                  <Icon icon="plus" />
                  {translations.createNew}
                </PlainLink>
              </Button>
            </PermissionsRequired>
          </ButtonContainer>
        </BrowserView>
      </Controls>

      <IssuesTable
        filter={filter}
        onSort={filterHandler('orderBy')}
        onFilterUpdate={({ count }) => {
          setFilteredCount(count)
        }}
      />
    </Wrapper>
  )
}
